import { NetInfoStateType } from '@react-native-community/netinfo';
import { StackNavigationProp } from '@react-navigation/stack';
import { AxiosError } from 'axios';

import { RouteNavigationParams } from 'router/types';

import { AsyncActionMetaOptions } from 'store/types';

import { EventParentalControlRating } from 'services/data-manager/types';

import LANGUAGES_NAMES from 'utils/Language/languageNames';

import {
    AddProfileMetaOptions,
    AddProfilePayloadOptions,
    DeleteProfilePayloadOptions,
    EditProfilePayloadOptions,
    MyListUpdateMetaOptions,
    ProfileMetaOptions,
    ProfilesListFetchMetaOptions,
    SilentLoginActionMetaOptions,
} from './actions.types';
import {
    ACCOUNT_INFO_SUCCESS,
    ADD_PROFILE,
    ADD_PROFILE_FAILURE,
    ADD_PROFILE_REQUEST,
    ADD_PROFILE_SUCCESS,
    CHANGE_PROFILE,
    CHANGE_USER_PROFILE,
    CHANGE_USER_PROFILE_FAILURE,
    CHANGE_USER_PROFILE_REQUEST,
    CHANGE_USER_PROFILE_SUCCESS,
    CREATE_FAVOURITES_CHANNELS_LIST,
    DELETE_FAVOURITES_CHANNELS_LIST,
    DELETE_FAVOURITES_CHANNELS_LIST_FAILURE,
    DELETE_FAVOURITES_CHANNELS_LIST_REQUEST,
    DELETE_FAVOURITES_CHANNELS_LIST_SUCCESS,
    DELETE_PROFILE,
    DELETE_PROFILE_FAILURE,
    DELETE_PROFILE_REQUEST,
    DELETE_PROFILE_REQUEST_RESET,
    DELETE_PROFILE_SUCCESS,
    EDIT_PROFILE,
    EDIT_PROFILE_FAILURE,
    EDIT_PROFILE_REQUEST,
    EDIT_PROFILE_REQUEST_RESET,
    EDIT_PROFILE_SUCCESS,
    GET_ENTITLEMENTS,
    GET_ENTITLEMENTS_FAILURE,
    GET_ENTITLEMENTS_REQUEST,
    GET_ENTITLEMENTS_SUCCESS,
    GET_FAVOURITES_CHANNELS_LIST,
    GET_FAVOURITES_CHANNELS_LIST_FAILURE,
    GET_FAVOURITES_CHANNELS_LIST_REQUEST,
    GET_FAVOURITES_CHANNELS_LIST_SUCCESS,
    GET_PINS_STATUS,
    GET_PINS_STATUS_FAILURE,
    GET_PINS_STATUS_REQUEST,
    GET_PINS_STATUS_SUCCESS,
    GET_PROFILE,
    GET_PROFILES_LIST,
    GET_PROFILES_LIST_FAILURE,
    GET_PROFILES_LIST_REQUEST,
    GET_PROFILES_LIST_SUCCESS,
    GET_PROFILE_FAILURE,
    GET_PROFILE_REQUEST,
    GET_PROFILE_SUCCESS,
    LOGIN_ERROR_RESET,
    LOGIN_FAILURE,
    LOGIN_REQUEST,
    LOGIN_SILENTLY,
    LOGIN_SILENTLY_FAILURE,
    LOGIN_SILENTLY_SUCCESS,
    LOGIN_SUCCESS,
    LOGIN_WITH_CREDENTIALS,
    LOGIN_WITH_SESSION_TOKEN,
    LOGOUT,
    LOGOUT_FAILURE,
    LOGOUT_SUCCESS,
    RESET_CASHED_PINS,
    RESET_CLIENT_ID,
    SELECT_USER_PROFILE_AVATAR,
    SELECT_USER_PROFILE_RATING,
    SET_CASHED_PIN,
    SET_CONNECTION_TYPE,
    SET_CURRENT_PROFILE_AVATAR,
    SET_LANGUAGE,
    SET_LAST_PROVISION_DATE,
    SET_LINEUP_CHANGED,
    SET_MODAL_VISIBLE,
    SET_OTT_LAST_CONNECTION,
    SET_REFRESH_TOKEN_ERROR,
    SET_SHOW_LINEUP_MODAL,
    SET_SUSPENSION_TYPE,
    UPDATE_FAVOURITES_CHANNELS_LIST,
    UPDATE_FAVOURITES_CHANNELS_LIST_FAILURE,
    UPDATE_FAVOURITES_CHANNELS_LIST_REQUEST,
    UPDATE_FAVOURITES_CHANNELS_LIST_SUCCESS,
    UPDATE_PROFILE_RELATED_DATA,
    UPDATE_RATINGS_THRESHOLDS,
    UPDATE_RATINGS_THRESHOLDS_CLEAN_REQUEST,
    UPDATE_RATINGS_THRESHOLDS_FAILURE,
    UPDATE_RATINGS_THRESHOLDS_REQUEST,
    UPDATE_RATINGS_THRESHOLDS_SUCCESS,
} from './constants';
import {
    AuthData,
    AuthError,
    EntitlementsResponse,
    FavouritesChannelsListsResponse,
    OriginalFavouriteChannelsListMetadata,
    ParentalControlRating,
    PinStatus,
    Profile,
    ProfileParentalRating,
    ProfilesListsResponse,
    SUSPENSION_TYPES,
} from './reducer.types';

export function setLanguage(language: keyof typeof LANGUAGES_NAMES) {
    return { type: SET_LANGUAGE, payload: { language } };
}

export function loginRequest() {
    return { type: LOGIN_REQUEST };
}

export function loginSuccess(auth: AuthData, currentUserProfileId?: string) {
    return {
        type: LOGIN_SUCCESS,
        payload: {
            auth,
            currentUserProfileId,
        },
    };
}

export function loginFailure(error: AuthError) {
    return { type: LOGIN_FAILURE, payload: { error } };
}

export function loginErrorReset() {
    return { type: LOGIN_ERROR_RESET };
}

export function loginSilently(
    meta: AsyncActionMetaOptions<any, AxiosError | string> = {},
) {
    return { type: LOGIN_SILENTLY, meta };
}

export function setRefreshTokenError(error: AxiosError | null) {
    return { type: SET_REFRESH_TOKEN_ERROR, payload: { error } };
}

export function loginSilentlyFailure(error: AxiosError | string) {
    return {
        type: LOGIN_SILENTLY_FAILURE,
        payload: { error },
    };
}

export function loginSilentlySuccess() {
    return {
        type: LOGIN_SILENTLY_SUCCESS,
    };
}

export function loginWithCredentials(
    email: string,
    password: string,
    meta: SilentLoginActionMetaOptions = {},
) {
    return {
        type: LOGIN_WITH_CREDENTIALS,
        payload: {
            email,
            password,
        },
        meta,
    };
}

export function loginWithSessionToken(
    sessionToken: string,
    meta: SilentLoginActionMetaOptions = {},
) {
    return {
        type: LOGIN_WITH_SESSION_TOKEN,
        payload: {
            sessionToken,
        },
        meta,
    };
}

export function logout(
    navigation: StackNavigationProp<RouteNavigationParams>,
    meta: AsyncActionMetaOptions<any, AxiosError> = {},
) {
    return { type: LOGOUT, payload: { navigation }, meta };
}

export function logoutSuccess() {
    return { type: LOGOUT_SUCCESS };
}

export function logoutFailure(error: AxiosError | null) {
    return { type: LOGOUT_FAILURE, payload: { error } };
}

export function getProfile(id: Profile['_id']) {
    return { type: GET_PROFILE, payload: { id } };
}

export function getProfileRequest() {
    return { type: GET_PROFILE_REQUEST };
}

export function getProfileSuccess(profile: Profile) {
    return { type: GET_PROFILE_SUCCESS, payload: { profile } };
}

export function getProfileFailure(error: any) {
    return { type: GET_PROFILE_FAILURE, payload: { error } };
}

export function changeProfile(profile: Profile) {
    return { type: CHANGE_PROFILE, payload: { profile } };
}

export function getEntitlements(
    meta: AsyncActionMetaOptions<
        EntitlementsResponse['resourceSet'],
        AxiosError
    > = {},
) {
    return {
        type: GET_ENTITLEMENTS,
        meta,
    };
}

export function getEntitlementsRequest() {
    return {
        type: GET_ENTITLEMENTS_REQUEST,
    };
}

export function getEntitlementsSuccess(
    entitlements: EntitlementsResponse['resourceSet'],
) {
    return {
        type: GET_ENTITLEMENTS_SUCCESS,
        payload: {
            entitlements,
        },
    };
}

export function getEntitlementsFailure(error: null | AxiosError) {
    return {
        type: GET_ENTITLEMENTS_FAILURE,
        payload: {
            error,
        },
    };
}

export function updateRatingsThresholds(
    rating: ParentalControlRating,
    meta: AsyncActionMetaOptions<
        {
            matchedCount: number;
            modifiedCount: number;
        },
        AxiosError
    > = {},
) {
    return {
        type: UPDATE_RATINGS_THRESHOLDS,
        payload: {
            rating,
        },
        meta,
    };
}

export function updateRatingsThresholdsRequest() {
    return {
        type: UPDATE_RATINGS_THRESHOLDS_REQUEST,
    };
}

export function updateRatingsThresholdsSuccess(rating: ParentalControlRating) {
    return {
        type: UPDATE_RATINGS_THRESHOLDS_SUCCESS,
        payload: {
            rating,
        },
    };
}

export function updateRatingsThresholdsFailure(error: null | AxiosError) {
    return {
        type: UPDATE_RATINGS_THRESHOLDS_FAILURE,
        payload: {
            error,
        },
    };
}

export function updateRatingsThresholdsCleanRequest() {
    return {
        type: UPDATE_RATINGS_THRESHOLDS_CLEAN_REQUEST,
    };
}

export function setModalVisible(visible: boolean) {
    return {
        type: SET_MODAL_VISIBLE,
        payload: {
            visible,
        },
    };
}

export function getPinsStatus(meta?: {
    onSuccess(pinsStatuses?: PinStatus[]): void;
    onFailure(error: any): void;
}) {
    return {
        type: GET_PINS_STATUS,
        meta,
    };
}

export function getPinsStatusRequest() {
    return {
        type: GET_PINS_STATUS_REQUEST,
    };
}

export function getPinsStatusSuccess(pinsStatus: PinStatus[]) {
    return {
        type: GET_PINS_STATUS_SUCCESS,
        payload: {
            pinsStatus,
        },
    };
}

export function getPinsStatusFailure(error: null | AxiosError) {
    return {
        type: GET_PINS_STATUS_FAILURE,
        payload: {
            error,
        },
    };
}

export function getFavouritesChannelsList(meta: AsyncActionMetaOptions = {}) {
    return { type: GET_FAVOURITES_CHANNELS_LIST, meta };
}

export function getFavouritesChannelsListRequest() {
    return { type: GET_FAVOURITES_CHANNELS_LIST_REQUEST };
}

export function getFavouritesChannelsListSuccess(
    favouritesLists: FavouritesChannelsListsResponse['favouritesLists'],
) {
    return {
        type: GET_FAVOURITES_CHANNELS_LIST_SUCCESS,
        payload: { favouritesLists },
    };
}

export function getFavouritesChannelsListFailure(error: any) {
    return { type: GET_FAVOURITES_CHANNELS_LIST_FAILURE, payload: { error } };
}

export function updateFavouritesChannelsList(
    favouritesList: FavouritesChannelsListsResponse['favouritesLists'][number],
    meta?: MyListUpdateMetaOptions,
) {
    return {
        type: UPDATE_FAVOURITES_CHANNELS_LIST,
        payload: { favouritesList },
        meta,
    };
}

export function updateFavouritesChannelsListRequest() {
    return { type: UPDATE_FAVOURITES_CHANNELS_LIST_REQUEST };
}

export function updateFavouritesChannelsListSuccess(
    updatedFavouritesList: FavouritesChannelsListsResponse['favouritesLists'][number],
) {
    return {
        type: UPDATE_FAVOURITES_CHANNELS_LIST_SUCCESS,
        payload: { updatedFavouritesList },
    };
}

export function updateFavouritesChannelsListFailure(error: AxiosError | null) {
    return {
        type: UPDATE_FAVOURITES_CHANNELS_LIST_FAILURE,
        payload: { error },
    };
}

export function createFavouritesChannelsList(
    favouritesList: OriginalFavouriteChannelsListMetadata['metadata'],
    meta: MyListUpdateMetaOptions,
    tags: string[],
) {
    return {
        type: CREATE_FAVOURITES_CHANNELS_LIST,
        payload: { favouritesList, tags },
        meta,
    };
}

export function resetFavouritesChannelsList(
    favouritesListId: string,
    meta: MyListUpdateMetaOptions,
) {
    return {
        type: DELETE_FAVOURITES_CHANNELS_LIST,
        payload: { favouritesListId },
        meta,
    };
}

export function resetFavouritesChannelsListRequest() {
    return { type: DELETE_FAVOURITES_CHANNELS_LIST_REQUEST };
}

export function resetFavouritesChannelsListSuccess() {
    return {
        type: DELETE_FAVOURITES_CHANNELS_LIST_SUCCESS,
    };
}

export function resetFavouritesChannelsListFailure(error: AxiosError | null) {
    return {
        type: DELETE_FAVOURITES_CHANNELS_LIST_FAILURE,
        payload: { error },
    };
}

export function getProfilesList(meta?: ProfilesListFetchMetaOptions) {
    return { type: GET_PROFILES_LIST, meta: meta || {} };
}

export function getProfilesListRequest() {
    return { type: GET_PROFILES_LIST_REQUEST };
}

export function getProfilesListSuccess(
    profilesLists: ProfilesListsResponse['resourceSet'],
) {
    return {
        type: GET_PROFILES_LIST_SUCCESS,
        payload: { profilesLists },
    };
}

export function getProfilesListFailure(error: any) {
    return { type: GET_PROFILES_LIST_FAILURE, payload: { error } };
}

export function changeUserProfile(
    newUserProfileId: string,
    meta: AsyncActionMetaOptions<string, AxiosError> = {},
) {
    return {
        type: CHANGE_USER_PROFILE,
        payload: { newUserProfileId },
        meta,
    };
}

export function changeUserProfileRequest() {
    return { type: CHANGE_USER_PROFILE_REQUEST };
}

export function changeUserProfileSuccess(
    {
        auth,
        currentUserProfileId,
    }: {
        auth: AuthData;
        currentUserProfileId: string;
    },
    meta: ProfileMetaOptions,
) {
    return {
        type: CHANGE_USER_PROFILE_SUCCESS,
        payload: { auth, currentUserProfileId },
        meta,
    };
}

export function changeUserProfileFailure(error: AxiosError | null) {
    return {
        type: CHANGE_USER_PROFILE_FAILURE,
        payload: { error },
    };
}

export function addProfile(
    {
        name,
        childProfile,
        ratingsThreshold,
        iconName,
    }: AddProfilePayloadOptions,
    meta: AddProfileMetaOptions,
) {
    return {
        type: ADD_PROFILE,
        payload: {
            name,
            childProfile,
            iconName,
            ratingsThresholds: [
                {
                    ratingsBody: 'HCRS',
                    ratingsThreshold,
                },
            ],
        },
        meta,
    };
}

export function addProfileRequest() {
    return { type: ADD_PROFILE_REQUEST };
}

export function addProfileSuccess(currentUserProfileId: string) {
    return {
        type: ADD_PROFILE_SUCCESS,
        payload: { currentUserProfileId },
    };
}

export function addProfileFailure(error: AxiosError | null) {
    return {
        type: ADD_PROFILE_FAILURE,
        payload: { error },
    };
}

export function editProfile(
    { id, name, ratingsThreshold, iconName }: EditProfilePayloadOptions,
    meta: ProfileMetaOptions,
) {
    return {
        type: EDIT_PROFILE,
        payload: {
            id,
            name,
            iconName,
            ...(ratingsThreshold && {
                ratingsThresholds: [
                    {
                        ratingsBody: 'HCRS',
                        ratingsThreshold,
                    },
                ],
            }),
        },
        meta,
    };
}

export function editProfileRequest() {
    return { type: EDIT_PROFILE_REQUEST };
}

export function editProfileSuccess() {
    return {
        type: EDIT_PROFILE_SUCCESS,
    };
}

export function editProfileFailure(error: AxiosError | null) {
    return {
        type: EDIT_PROFILE_FAILURE,
        payload: { error },
    };
}

export function editProfileRequestReset() {
    return {
        type: EDIT_PROFILE_REQUEST_RESET,
    };
}

export function deleteProfile(
    { id }: DeleteProfilePayloadOptions,
    meta: ProfileMetaOptions,
) {
    return {
        type: DELETE_PROFILE,
        payload: {
            id,
        },
        meta,
    };
}

export function deleteProfileRequest() {
    return { type: DELETE_PROFILE_REQUEST };
}

export function deleteProfileSuccess() {
    return {
        type: DELETE_PROFILE_SUCCESS,
    };
}

export function deleteProfileFailure(error: AxiosError | null) {
    return {
        type: DELETE_PROFILE_FAILURE,
        payload: { error },
    };
}

export function changeUserProfileRating(
    profileParentalRating: ProfileParentalRating | null,
) {
    return {
        type: SELECT_USER_PROFILE_RATING,
        payload: profileParentalRating,
    };
}

export function deleteProfileRequestReset() {
    return {
        type: DELETE_PROFILE_REQUEST_RESET,
    };
}

export function changeUserProfileAvatar(profileAvatar: string | null) {
    return {
        type: SELECT_USER_PROFILE_AVATAR,
        payload: profileAvatar,
    };
}

export function setCurrentProfileAvatar(profileAvatar: string | undefined) {
    return {
        type: SET_CURRENT_PROFILE_AVATAR,
        payload: profileAvatar,
    };
}

export function setOTTLastConnection(date: string | undefined) {
    return {
        type: SET_OTT_LAST_CONNECTION,
        payload: date,
    };
}

export function setLastProvisionDate(date: string | undefined) {
    return {
        type: SET_LAST_PROVISION_DATE,
        payload: date,
    };
}

export function setSuspensionType(suspension: SUSPENSION_TYPES | undefined) {
    return { type: SET_SUSPENSION_TYPE, payload: { suspension } };
}

export function resetClientId() {
    return { type: RESET_CLIENT_ID };
}

export function resetCashedPins() {
    return { type: RESET_CASHED_PINS };
}

export function setCashedPin(
    profileId: string,
    pinLimit?: EventParentalControlRating,
    timestamp?: number,
) {
    return {
        type: SET_CASHED_PIN,
        payload: { profileId, pinLimit, timestamp },
    };
}

export function setLineupChanged(isLineupChanged: boolean) {
    return {
        type: SET_LINEUP_CHANGED,
        payload: { isLineupChanged },
    };
}

export function setShowLineupModal(shouldLineupModalShow: boolean) {
    return {
        type: SET_SHOW_LINEUP_MODAL,
        payload: { shouldLineupModalShow },
    };
}

export function updateProfileRelatedData(
    resolve: (value: unknown) => void,
    reject: (error: unknown) => void,
) {
    return { type: UPDATE_PROFILE_RELATED_DATA, resolve, reject };
}

export function setConnectionType(
    connectionType: NetInfoStateType | undefined,
) {
    return {
        type: SET_CONNECTION_TYPE,
        payload: { connectionType },
    };
}

export function accountInfoSuccess(sharedConsentPublicId?: string) {
    return {
        type: ACCOUNT_INFO_SUCCESS,
        payload: {
            sharedConsentPublicId,
        },
    };
}
